/**
 * ============================================================================
 * sanitizeHtml.js - Rimozione Style Inline Non Desiderati
 * ============================================================================
 * 
 * Rimuove stili inline e attributi deprecated che vengono spesso generati da
 * editor WYSIWYG (come Word, Google Docs) quando il contenuto HTML viene
 * copiato incollato nel CMS.
 * 
 * USO PRIMARIO:
 * • Pulire HTML contenente font-size, font-family, colori inline
 * • Rimuovere attributi deprecated come <font face="" size="" color="">
 * • Preparare contenuto HTML per il rendering con stili controllati via CSS
 * 
 * STILI RIMOSSI:
 * • font-size: 12px; (tutti i formati)
 * • font-family: 'Arial', sans-serif; (anche con virgole, virgolette)
 * • color: #fff; (RGB, RGBA, HSL, keywords)
 * • background-color e ogni altro *-color: PRESERVATI (non rimossi)
 * 
 * ATTRIBUTI RIMOSSI:
 * • <font face="Arial">
 * • <font size="3">
 * • <font color="#fff">
 * 
 * LOGICA DI CLEANUP:
 * 1. Rimuove singole proprietà CSS problematiche con regex patterns
 * 2. Pulisce gli attributi style="" vuoti o solo con semicoloni
 * 3. Rimuove attributi deprecated dei tag <font>
 * ============================================================================
 */

/**
 * Sanitizza HTML rimuovendo stili inline non desiderati
 * 
 * @param {string} html - HTML string da pulire
 * @returns {string} HTML pulito
 * 
 * ESEMPIO:
 * Input:  '<p style="font-size: 12px; color: red;">Testo</p>'
 * Output: '<p style="">Testo</p>'
 */
export function sanitizeHtmlRegex(html) {
  if (!html) return '';
  
  // ─────────────────────────────────────────────────────────────
  // STEP 1: Rimuovi proprietà CSS problematiche
  // ─────────────────────────────────────────────────────────────
  
  const patterns = [
    // font-size con vari formati (px, rem, em, %, etc)
    // Gestisce anche quoted values e parentesi (es. calc())
    /font-size\s*:\s*(?:[^;}"']|"[^"]*"|'[^']*')+;?/gi,
    
    // font-family (può contenere virgole, virgolette, spazi)
    // Esempi: 'Arial', sans-serif | "Times New Roman"
    /font-family\s*:\s*(?:[^;}"']|"[^"]*"|'[^']*')+;?/gi
  ];
  
  // Applica tutti i pattern di rimozione
  patterns.forEach(pattern => {
    html = html.replace(pattern, '');
  });
  
  // color SOLO per foreground: background-color, border-color, outline-color,
  // caret-color, text-decoration-color ecc. vanno preservati.
  //
  // Il prefisso viene CATTURATO invece di essere guardato all'indietro con un
  // negative lookbehind (?<!background-): il lookbehind e' un errore di
  // sintassi su Safari < 16.4 (iOS < 16.4) e fa fallire il parsing dell'INTERO
  // bundle, non solo di questa funzione.
  //
  // Se davanti a "color" c'e' qualcosa (background-, border-, ...) si tiene
  // tutto il match; se "color" e' nudo, si rimuove.
  html = html.replace(
    /([-\w]*)color\s*:\s*(?:[^;}"']|"[^"]*"|'[^']*')+;?/gi,
    (match, prefix) => (prefix ? match : '')
  );
  
  // ─────────────────────────────────────────────────────────────
  // STEP 2: Pulisci attributi style vuoti
  // ─────────────────────────────────────────────────────────────
  
  // Rimuovi style="" con solo spazi: style="   "
  html = html.replace(/style\s*=\s*["']\s*;*\s*["']/gi, '');
  
  // Normalizza style="" con solo semicoloni: style=";;;" → style=""
  html = html.replace(/style\s*=\s*["'](\s*;\s*)+["']/gi, 'style=""');
  
  // ─────────────────────────────────────────────────────────────
  // STEP 3: Rimuovi attributi deprecated dei tag <font>
  // ─────────────────────────────────────────────────────────────
  
  // <font face="Arial"> → <font>
  html = html.replace(/\s*face\s*=\s*["'][^"']*["']/gi, '');
  
  // <font size="3"> → <font>
  html = html.replace(/\s*size\s*=\s*["'][^"']*["']/gi, '');
  
  // <font color="#fff"> → <font>
  html = html.replace(/\s*color\s*=\s*["'][^"']*["']/gi, '');
  
  return html;
}